<script setup>
import { computed } from 'vue';

const props = defineProps({
  group: { type: Object, required: true },
  txns: { type: Array, default: () => [] },
  loading: { type: Boolean, default: false },
  expanded: { type: Boolean, default: false },
  dragKind: { type: String, default: null }, // 'txn' | 'group' | null
  accepts: { type: Boolean, default: false }, // txn için geçerli hedef mi
  highlight: { type: Boolean, default: false }, // txn hedef vurgusu
  insertEdge: { type: String, default: '' }, // grup için 'top' | 'bottom'
  muted: { type: Boolean, default: false }, // boş grup (kalem yok)
  isFirst: { type: Boolean, default: false },
  isLast: { type: Boolean, default: false },
});

const emit = defineEmits([
  'toggle',
  'moveUp',
  'moveDown',
  'cycleSide',
  'togglePnl',
  'groupDragstart',
  'groupDragend',
  'groupDragover',
  'cardDragover',
  'cardDragleave',
  'cardDrop',
  'txnDragstart',
  'txnDragend',
]);

const tl = v =>
  (v == null ? 0 : v).toLocaleString('tr-TR', {
    style: 'currency',
    currency: 'TRY',
    maximumFractionDigits: 0,
  });

const sideColor = computed(() => {
  if (props.group.side === 'income')
    return 'text-green-700 dark:text-green-400';
  if (props.group.side === 'expense') return 'text-ruby-700 dark:text-ruby-400';
  return 'text-slate-500 dark:text-slate-300';
});

const borderClass = computed(() => {
  if (props.highlight) return 'border-woot-400 ring-2 ring-woot-300';
  if (props.accepts) return 'border-woot-200 border-dashed';
  return 'border-slate-100 dark:border-slate-700';
});

const onDragOver = e => {
  if (props.dragKind === 'group') {
    e.preventDefault();
    const r = e.currentTarget.getBoundingClientRect();
    emit('groupDragover', e.clientY > r.top + r.height / 2 ? 'bottom' : 'top');
    return;
  }
  if (props.dragKind === 'txn' && props.accepts) {
    e.preventDefault();
    emit('cardDragover');
  }
};
</script>

<template>
  <div
    class="relative overflow-hidden bg-white border rounded-xl shadow-sm dark:bg-slate-800 transition-colors"
    :class="[borderClass, muted ? 'opacity-50' : '']"
    @dragover.stop="onDragOver"
    @dragleave="emit('cardDragleave')"
    @drop.stop="emit('cardDrop')"
  >
    <!-- Grup ekleme çizgisi (kaymaz, üstte/altta) -->
    <div
      v-if="insertEdge === 'top'"
      class="absolute inset-x-0 top-0 z-20 h-1.5 bg-woot-500"
    />
    <div
      v-if="insertEdge === 'bottom'"
      class="absolute inset-x-0 bottom-0 z-20 h-1.5 bg-woot-500"
    />

    <!-- Başlık: sürükle tutamağı + aç/kapa + kontroller -->
    <div
      class="flex items-center gap-1 px-2 py-2.5"
      draggable="true"
      @dragstart="emit('groupDragstart')"
      @dragend="emit('groupDragend')"
    >
      <span
        class="px-1 cursor-grab active:cursor-grabbing text-slate-300 select-none"
        title="Grubu taşımak için sürükle"
        >⠿</span
      >
      <button
        class="flex items-center gap-2 text-left grow min-w-0"
        @click="emit('toggle')"
      >
        <span class="text-lg shrink-0">{{ group.icon }}</span>
        <span class="font-medium truncate text-slate-800 dark:text-slate-100">{{
          group.label
        }}</span>
        <span
          class="text-slate-300 transition-transform shrink-0"
          :class="expanded ? 'rotate-90' : ''"
          >›</span
        >
      </button>
      <span
        v-if="group.pnl"
        class="px-1.5 py-0.5 text-[10px] font-bold rounded shrink-0 bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300"
        title="Kâr-Zarara Esas Gider'e dahil"
        >K/Z</span
      >
      <span class="text-xs text-slate-400 shrink-0 hidden sm:inline"
        >{{ group.count }} adet</span
      >
      <span class="font-semibold tabular-nums shrink-0" :class="sideColor">{{
        tl(group.total)
      }}</span>
      <!-- Sıra + taraf kontrolleri -->
      <div class="flex items-center ml-1 shrink-0">
        <button
          class="px-1 text-slate-400 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30"
          title="Yukarı taşı"
          :disabled="isFirst"
          @click.stop="emit('moveUp')"
        >
          ▲
        </button>
        <button
          class="px-1 text-slate-400 rounded hover:bg-slate-100 dark:hover:bg-slate-700 disabled:opacity-30"
          title="Aşağı taşı"
          :disabled="isLast"
          @click.stop="emit('moveDown')"
        >
          ▼
        </button>
        <button
          class="px-1 text-slate-400 rounded hover:bg-slate-100 dark:hover:bg-slate-700"
          title="Tarafı değiştir (Gelir → Gider → Nötr)"
          @click.stop="emit('cycleSide')"
        >
          ⇄
        </button>
        <button
          v-if="group.side === 'expense'"
          class="px-1 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/40"
          :class="
            group.pnl
              ? 'text-indigo-600 dark:text-indigo-300'
              : 'text-slate-300 dark:text-slate-500'
          "
          :title="
            group.pnl
              ? 'Kâr-Zarara Esas Gider\'den çıkar'
              : 'Kâr-Zarara Esas Gider\'e ekle'
          "
          @click.stop="emit('togglePnl')"
        >
          🎯
        </button>
      </div>
    </div>

    <!-- Hareketler -->
    <div
      v-if="expanded"
      class="border-t border-slate-100 dark:border-slate-700 max-h-[420px] overflow-y-auto"
    >
      <div v-if="loading" class="px-3 py-4 text-sm text-slate-400">
        Yükleniyor…
      </div>
      <div v-else-if="!txns.length" class="px-3 py-4 text-sm text-slate-400">
        Bu grupta hareket yok.
      </div>
      <div
        v-for="t in txns"
        :key="t.id"
        draggable="true"
        class="flex items-center gap-2 px-3 py-2 text-sm border-b cursor-grab active:cursor-grabbing border-slate-50 dark:border-slate-700/50 transition-colors hover:bg-slate-50 dark:hover:bg-slate-700/30"
        @dragstart="emit('txnDragstart', t)"
        @dragend="emit('txnDragend')"
      >
        <span class="text-slate-300 shrink-0">⠿</span>
        <span
          v-if="t.is_recent"
          class="px-1.5 py-0.5 text-xs font-bold rounded shrink-0 bg-woot-500 text-white"
          >Yeni</span
        >
        <span class="text-xs whitespace-nowrap text-slate-400 shrink-0">{{
          t.txn_on
        }}</span>
        <span
          class="min-w-0 truncate grow"
          :title="
            (t.counterparty ? t.counterparty + ' — ' : '') + t.description
          "
        >
          <span
            v-if="t.counterparty"
            class="inline-block px-1.5 py-0.5 mr-1 text-xs font-medium rounded bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-200"
            >{{ t.counterparty }}</span
          >
          <span class="text-xs text-slate-400">{{ t.description }}</span>
        </span>
        <span
          class="font-medium tabular-nums shrink-0"
          :class="
            t.amount >= 0
              ? 'text-green-700 dark:text-green-400'
              : 'text-ruby-700 dark:text-ruby-400'
          "
          >{{ tl(t.amount) }}</span
        >
      </div>
    </div>
  </div>
</template>
